import { Client, Conversation } from "@twilio/conversations";
import { Dispatch } from "redux";
import log from "loglevel";

import { initMessagesListener } from "./listeners/messagesListener";
import { initParticipantsListener } from "./listeners/participantsListener";
import { initConversationListener } from "./listeners/conversationListener";
import { ConfigState, EngagementPhase } from "../definitions";
import { initClientListeners } from "./listeners/clientListener";
import { notifications } from "../../notifications";
import { ACTION_START_SESSION, ACTION_LOAD_CONFIG, ACTION_UPDATE_SESSION_ERROR_MESSAGE } from "./actionTypes";
import { MESSAGES_LOAD_COUNT } from "../../constants";
import { addNotification, changeEngagementPhase, updatePreEngagementData } from "./genericActions";
import {
    PreEngagementConfig,
    PreEngagementField,
    RootState,
    SessionData,
    SessionInitializationMode
} from "../../definitions";
import { sessionDataHandler } from "../../sessionDataHandler";
import { isConversationClosed } from "../../utils/conversations";

const handleEndedConversation = (dispatch: Dispatch) => () => {
    sessionDataHandler.clear();
    dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
};

const persistInitializedSession = (sessionData: SessionData) => {
    sessionDataHandler.setStoredSessionData(sessionData);
};

const notifyWebchatEventListeners = async (
    webchatEventListeners: RootState["webchatEventListeners"]["listeners"],
    event: string,
    conversation: Conversation
) => {
    const listenerPromises = webchatEventListeners
        .filter((listener) => listener.hasOwnProperty(event))
        .map(async (listener) => listener[event]?.({ conversation }));

    await Promise.all(listenerPromises);
};

export function initConfig(config: ConfigState) {
    return {
        type: ACTION_LOAD_CONFIG,
        payload: config
    };
}

export function initSession(
    { token, conversationSid, expiration }: SessionData,
    { mode }: { mode: SessionInitializationMode }
) {
    return async (dispatch: Dispatch, getState?: () => RootState) => {
        let conversationsClient: Client;
        let conversation: Conversation;
        let participants;
        let users;
        let messages;

        try {
            dispatch({
                type: ACTION_UPDATE_SESSION_ERROR_MESSAGE,
                payload: { error: "" }
            });
            conversationsClient = await Client.create(token);
            try {
                conversation = await conversationsClient.getConversationBySid(conversationSid);
                if (isConversationClosed(conversation)) {
                    handleEndedConversation(dispatch)();
                    return;
                }
            } catch (e) {
                dispatch(addNotification(notifications.failedToInitSessionNotification("Couldn't load conversation")));
                dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
                return;
            }

            participants = await conversation.getParticipants();
            users = await Promise.all(participants.map(async (p) => p.getUser()));
            const webchatEventListeners = getState?.().webchatEventListeners?.listeners ?? [];
            if (mode === "engagementStart") {
                await notifyWebchatEventListeners(webchatEventListeners, "afterStartEngagement", conversation);
            }
            if (mode !== "storedRestore") {
                await notifyWebchatEventListeners(webchatEventListeners, "afterSessionInitialized", conversation);
            }
            messages = (await conversation.getMessages(MESSAGES_LOAD_COUNT)).items;
        } catch (e) {
            log.error("Something went wrong when initializing session", e);
            throw e;
        }

        persistInitializedSession({
            token,
            conversationSid,
            expiration
        });

        dispatch({
            type: ACTION_START_SESSION,
            payload: {
                token,
                conversationSid,
                conversationsClient,
                conversation,
                users,
                participants,
                messages,
                conversationState: conversation.state?.current,
                currentPhase: EngagementPhase.MessagingCanvas
            }
        });
        const contextData = getState?.().config?.context;
        initClientListeners(conversationsClient, dispatch, contextData);
        initConversationListener(conversation, dispatch);
        initMessagesListener(conversation, dispatch);
        initParticipantsListener(conversation, dispatch, conversationsClient);
    };
}

const getDefaultFieldValue = (field: PreEngagementField) => {
    if (field.attributes.type === "checkbox") {
        return field.attributes.hasOwnProperty("value") ? Boolean(field.attributes.value) : false;
    }
    return field.attributes.hasOwnProperty("value") ? field.attributes.value : "";
};
export const initPreEngagementFormState = (
    preEngagementConfig: PreEngagementConfig,
    context?: { [key: string]: string }
) => {
    const preEngagementFormState: { [key: string]: any } = {};
    preEngagementConfig.fields
        .filter((field) => {
            if (context?.[field.attributes.name]) {
                return false;
            }
            return true;
        })
        .forEach((field) => {
            if (field.attributes.name !== "dummy_field") {
                preEngagementFormState[field.attributes.name] = getDefaultFieldValue(field);
            }
        });
    return updatePreEngagementData(preEngagementFormState);
};
